/**
 * Start Node Component
 *
 * Entry point node for workflows.
 * Defines environment variables and enables built-in parameters.
 */

import { memo } from 'react';
import { type NodeProps } from '@xyflow/react';
import { BaseNode } from './BaseNode';
import type { NodeData, CustomVariable, BuiltInVariables } from '@/types/imageWorkflow';

// Built-in variable labels for display
const BUILTIN_LABELS: Record<keyof BuiltInVariables, string> = {
  user_id: '用户 ID',
  user_name: '用户名',
  user_email: '用户邮箱',
  execution_id: '执行 ID',
  workflow_id: '工作流 ID',
  workflow_name: '工作流名称',
  timestamp: '时间戳',
  date: '日期',
  time: '时间',
};

const StartNodeComponent = ({ id, data, selected }: NodeProps) => {
  const nodeData = data as NodeData;
  const variables = nodeData.variables || [];
  const enabledBuiltins = nodeData.enabled_builtins || ['user_id', 'user_name'];

  return (
    <BaseNode id={id} data={nodeData} type="start" selected={selected} hasTargetHandle={false}>
      <div className="space-y-2">
        {/* Built-in Variables Summary */}
        <div className="text-xs">
          <span className="text-gray-500">内置变量: </span>
          {enabledBuiltins.length > 0 ? (
            <span className="text-cyan-700">
              {enabledBuiltins.slice(0, 3).map((key) => BUILTIN_LABELS[key]).join(', ')}
              {enabledBuiltins.length > 3 && ` +${enabledBuiltins.length - 3}`}
            </span>
          ) : (
            <span className="text-gray-400 italic">未启用</span>
          )}
        </div>

        {/* Custom Variables Summary */}
        <div className="text-xs">
          <span className="text-gray-500">自定义变量: </span>
          {variables.length > 0 ? (
            <span className="text-cyan-700">
              {variables.slice(0, 2).map((v: CustomVariable) => v.key).join(', ')}
              {variables.length > 2 && ` +${variables.length - 2}`}
            </span>
          ) : (
            <span className="text-gray-400 italic">未定义</span>
          )}
        </div>
      </div>
    </BaseNode>
  );
};

export const StartNode = memo(StartNodeComponent);
export default StartNode;
